import { Component, Input, Output, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { DataService } from '../../../core/data.service';

export interface FileDropResult {
  name: string;
  key: string;
}

@Component({
  standalone: false,
  selector: 'app-file-drop',
  templateUrl: './file-drop.component.html',
  styleUrls: ['./file-drop.component.scss']
})
export class FileDropComponent {
  @Input() fileTitle = 'Upload File';
  @Input() uploadUrl = '';
  @Input() fileKey = '';
  @Input() existingFilename = '';
  @Output() uploaded = new EventEmitter<FileDropResult>();
  @Output() deleted = new EventEmitter<void>();

  @Input() maxSizeMB = 5000; // Default 5GB max for ISOs
  @Input() allowedExtensions = '.pdf,.jpg,.jpeg,.png,.gif,.bmp,.iso,.img,.zip,.csv,.xlsx,.xls,.doc,.docx,.txt';

  selectedFile: File | null = null;
  uploading = false;
  uploadProgress = 0;
  dragOver = false;
  validationError = '';
  deleting = false;

  // Blocked executable extensions
  private readonly blockedExtensions = ['.exe', '.bat', '.cmd', '.ps1', '.vbs', '.js', '.msi', '.scr', '.com', '.pif', '.hta'];

  constructor(
    private http: HttpClient,
    private messageSvc: MessageService,
    private dataSvc: DataService
  ) {}

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = true;
  }

  onDragLeave(): void {
    this.dragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;
    const files = event.dataTransfer?.files;
    if (files?.length && this.validateFile(files[0])) {
      this.selectedFile = files[0];
      this.startUpload();
    }
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length && this.validateFile(input.files[0])) {
      this.selectedFile = input.files[0];
      this.startUpload();
    }
  }

  private validateFile(file: File): boolean {
    this.validationError = '';
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();

    // Block dangerous extensions
    if (this.blockedExtensions.includes(ext)) {
      this.validationError = `File type "${ext}" is not allowed for security reasons.`;
      this.messageSvc.add({ severity: 'error', summary: 'Blocked', detail: this.validationError });
      return false;
    }

    // Enforce allowedExtensions allow-list if one is configured
    const allowed = (this.allowedExtensions || '')
      .split(',')
      .map(e => e.trim().toLowerCase())
      .filter(Boolean);
    if (allowed.length && !allowed.includes(ext)) {
      this.validationError = `Only ${allowed.join(', ')} files are allowed.`;
      this.messageSvc.add({ severity: 'error', summary: 'Invalid type', detail: this.validationError });
      return false;
    }

    // Check size
    const maxBytes = this.maxSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      this.validationError = `File exceeds maximum size of ${this.maxSizeMB}MB.`;
      this.messageSvc.add({ severity: 'error', summary: 'Too Large', detail: this.validationError });
      return false;
    }

    return true;
  }

  startUpload(): void {
    if (!this.selectedFile || !this.uploadUrl) return;
    this.uploading = true;
    this.uploadProgress = 0;

    const formData = new FormData();
    formData.append('file', this.selectedFile);
    if (this.fileKey) formData.append('filekey', this.fileKey);

    this.http.post<any>(this.uploadUrl, formData, {
      reportProgress: true,
      observe: 'events',
      withCredentials: true
    }).subscribe({
      next: (event: any) => {
        if (event.type === 1 && event.total) {
          this.uploadProgress = Math.round((event.loaded / event.total) * 100);
        }
        if (event.type === 4) {
          this.uploading = false;
          this.uploadProgress = 100;
          const body = event.body || {};
          const uploaded = Array.isArray(body.files) && body.files[0] ? body.files[0] : null;
          const name = uploaded?.name || this.selectedFile!.name;
          const key = uploaded?.deleteUrl || '';
          this.existingFilename = name;
          this.fileKey = key;
          this.uploaded.emit({ name, key });
          this.messageSvc.add({ severity: 'success', summary: 'Uploaded', detail: `${this.fileTitle} uploaded.` });
        }
      },
      error: () => {
        this.uploading = false;
        this.messageSvc.add({ severity: 'error', summary: 'Error', detail: 'Upload failed.' });
      }
    });
  }

  deleteFile(): void {
    if (!this.fileKey || this.deleting) return;
    this.deleting = true;
    this.dataSvc.dsSave([{
      table: 'qta',
      type: 'delete',
      source: 'custportal',
      rows: [{ keys: { anykey: this.fileKey } }]
    }]).subscribe({
      next: () => {
        this.existingFilename = '';
        this.fileKey = '';
        this.deleting = false;
        this.deleted.emit();
      },
      error: () => {
        this.deleting = false;
        this.messageSvc.add({ severity: 'error', summary: 'Error', detail: 'Delete failed.' });
      }
    });
  }
}
